import { cloneDeep, debounce, get, startsWith } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import {
  Attribute,
  DropdownAttribute,
  SearchHandler,
  SearchResult,
  Variable
} from '@/components/Form/AudienceTargeting/Audience/interfaces'
import { withField, withFieldProps } from '@/components/Form/FieldWrappers/withField'
import { Organization, Person } from '@/components/Icons'
import ComboBox, { ComboBoxOption } from '@/components/TailwindUI/ComboBox'
import VariableAutoComplete from '@/pages/SpaceAppBuilder/components/VariableInputDropdown'
import { FieldTypes } from '@/pages/SpaceAppBuilder/utilities/interfaces'

import AttributeFieldResult from './AttributeFieldResult'
import { getSearchHandlerFromType } from './SearchHandlers'

const StyledVariableAutoComplete = styled(VariableAutoComplete)`
  &&.form__field {
    margin-left: 0;
    width: 100%;
  }
`

const renderResult = (props: any) => <AttributeFieldResult {...props} />

const debouncedFetch = debounce(
  async (
    searchHandler: SearchHandler,
    searchTerm: string,
    attribute: DropdownAttribute,
    done: (results: SearchResult[]) => void
  ) => {
    let trimmedValue = searchTerm.trim()

    if (searchTerm !== 'true' && searchTerm !== 'false') {
      trimmedValue = JSON.stringify(trimmedValue)
    }

    const result = await searchHandler.search(trimmedValue)
    done(result)
  },
  1000
)

interface AttributeSearchProps {
  attribute: DropdownAttribute
  variables?: Variable[]
  initialValue: { value: string; displayValue: string; subtitle?: string }
  aggregateAttributes: Attribute[]
  onSelect: (selectedValue: { value: string; displayValue: string; subtitle?: string }, result: Attribute) => void
  readOnly?: boolean
}
const AttributeSearch = ({
  name,
  attribute,
  aggregateAttributes,
  variables,
  onSelect,
  readOnly = false,
  form,
  initialValue
}: AttributeSearchProps & withFieldProps) => {
  const [results, setResults] = useState<SearchResult[] & Partial<Variable>[]>([])
  const [isLoading, setLoading] = useState(false)
  const [searchHandler, setSearchHandler] = useState(
    new (getSearchHandlerFromType(attribute.type))(attribute.attribute as Attribute, aggregateAttributes)
  )
  const isVariable = (string: string | undefined) => startsWith(string, '$.')
  const [attributeOptions, setAttributeOptions] = useState<ComboBoxOption[]>([])

  useEffect(() => {
    if (variables) {
      setResults([])
      setLoading(false)
    }
    setSearchHandler(
      new (getSearchHandlerFromType(attribute.type))(attribute.attribute as Attribute, aggregateAttributes)
    )
  }, [aggregateAttributes, attribute.attribute, attribute.type, variables])

  useEffect(() => {
    let options = [] as ComboBoxOption[]
    if (results?.[0]?.type === 'group') {
      options = results.map((searchResult: SearchResult) => {
        return {
          label: searchResult.displayName || searchResult.displayValue || searchResult?.term?.toString() || '',
          displayText: `(${searchResult.subtitle} Members)`,
          value: searchResult?.term?.toString() || '',
          icon: <Organization />
        }
      })
    } else if (results?.[0]?.type === 'user' || results?.[0]?.type === 'individual') {
      options = results.map((searchResult: SearchResult) => {
        return {
          label: searchResult.displayName || searchResult.term?.toString() || '',
          value: searchResult.termId?.toString() || '',
          displayText: searchResult.subtitle || '',
          icon: <Person />
        }
      })
    }
    if (options?.length === 0 && initialValue?.value) {
      options.unshift({ label: initialValue.displayValue, value: initialValue.value?.toString() })
    }
    setAttributeOptions(options)
  }, [results, initialValue.displayValue, initialValue.value])

  return useMemo(() => {
    const onTextUpdate = (value: string) => {
      if (value.length > 0) {
        setLoading(true)
        debouncedFetch(searchHandler, value, attribute, results => {
          setResults(results.map(result => ({ ...result, value: result.termId })))
          setLoading(false)
        })
      } else {
        setResults([])
      }
    }

    const onResultClick = (result: SearchResult) => {
      const newAttr = searchHandler.getAttribute()
      const newValue = searchHandler.getValue(result)
      if (isVariable(result.name)) {
        const modifiedAttr = cloneDeep(newAttr)
        if (modifiedAttr.id === 'individual') {
          modifiedAttr.urn = 'userName'
        }
        onSelect({ value: result.name || '', displayValue: result.name || '' }, modifiedAttr)
      } else {
        onSelect(newValue, newAttr)
      }

      return { display: newValue.displayValue, value: newValue.value }
    }

    const showError = get(form.errors, `${name}`) && !!get(form.touched, `${name}`)

    return (
      <div className="form__field">
        {variables ? (
          <StyledVariableAutoComplete
            name={name}
            variables={variables}
            isSearching={attribute.id !== 'collectionEmails' && isLoading}
            description={attribute.id === 'collectionEmails' ? 'Variable should contain an array of emails' : ''}
            options={results}
            showOptions={initialValue.displayValue || results}
            defaultValue={
              initialValue.displayValue && initialValue.displayValue !== 'undefined'
                ? { name: initialValue.displayValue, value: initialValue.value, subtitle: initialValue.subtitle }
                : undefined
            }
            onSearch={onTextUpdate}
            renderOption={(props: any) => renderResult({ type: attribute.id, ...props })}
            validVariableTypes={attribute.id === 'collectionEmails' ? [FieldTypes.ARRAY] : [FieldTypes.STRING]}
            placeholder={attribute.placeholder || attribute.displayName}
            onSelectOption={onResultClick}
            readOnly={readOnly}
          />
        ) : (
          <>
            <ComboBox
              name={name}
              error={showError}
              isAsync={true}
              disabled={readOnly}
              value={initialValue.value}
              placeholder={attribute.placeholder || attribute.displayName}
              isLoading={isLoading}
              options={attributeOptions}
              onSelect={(option: ComboBoxOption) =>
                onResultClick({
                  termId: `${option.value}`,
                  term: `${option.label}`
                } as SearchResult)
              }
              onChange={onTextUpdate}
            />
            {attribute.id === 'collectionEmails' && <p>Variable should contain an array of emails</p>}
          </>
        )}
      </div>
    )
  }, [
    attribute,
    form.errors,
    form.touched,
    isLoading,
    name,
    onSelect,
    attributeOptions,
    searchHandler,
    initialValue,
    results,
    variables,
    readOnly
  ])
}

AttributeSearch.displayName = 'AttributeSearch'
export default withField(AttributeSearch)
